import React from 'react';
import { EditItemButton } from './EditItemButton';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { PlayBadge } from './DestaqueCard';
import {
  getRedesCardImage,
  youtubeThumb,
  type RedesCard,
  type YoutubeVideoRef,
} from './helpers';
import { comPrefixo } from 'volto-ministerio-publico-federal/helpers/Url/subpath';
import {
  isSocialGlyphToken,
  SocialIcon,
  SOCIAL_LABELS,
} from 'volto-ministerio-publico-federal/components/Icons/social';

/** Escala da imagem dos cards. A mesma dos demais cards curados da capa. */
const REDES_IMAGE_SCALE = 'larger';

/* Caixa de nível de bloco, no lugar da utility `block` do Tailwind.
   `.block` é também o nome da classe do wrapper de bloco do Volto, que carrega
   `margin-bottom: 1em` global — a utility de display herdava essa margem e
   empurrava o título 16px para baixo da imagem (mais 16 do `<a>` em volta).
   `flex` entrega a mesma caixa de bloco sem colidir com nome de componente.
   Mesma família da colisão `.fixed` do Tailwind com a tabela do Semantic. */
const BLOCO = 'flex';

interface RedeCardProps {
  card: RedesCard;
  tall?: boolean;
  /**
   * Card de slide do carrossel (abaixo de `lg`): imagem 4:3, ícone da rede e
   * legenda menores no celular, onde o slide tem ~140px.
   */
  slide?: boolean;
  onEditItem?: (slot: number) => void;
  onPlay: (video: YoutubeVideoRef) => void;
}

/**
 * Card de post da faixa "Acompanhe nossas redes".
 *
 * Vídeo do YouTube abre no modal (botão); qualquer outra rede é um link
 * externo. O ícone da rede fica sobre a imagem, no canto inferior esquerdo, e
 * vem sempre — mesmo sem título nem capa, porque é derivado da URL.
 *
 * Mora em arquivo próprio porque os dois ramos o renderizam: a grade 2×2+1 do
 * desktop e o carrossel do mobile (`AcompanheRedesCarouselMobile`), além do
 * card solto do 5º item abaixo dele — mesma razão de `IniciativaOverlayCard`.
 */
export const RedeCard: React.FC<RedeCardProps> = ({
  card,
  tall,
  slide,
  onEditItem,
  onPlay,
}) => {
  const image = getRedesCardImage(card, REDES_IMAGE_SCALE);
  const isVideo = !!card.videoId;
  const network = isSocialGlyphToken(card.network) ? card.network : undefined;
  const label =
    card.title || (network ? `Post no ${SOCIAL_LABELS[network]}` : 'Post');
  // O `aria-label` do gatilho substitui o conteúdo na leitura, então o `alt`
  // da imagem sozinho nunca seria anunciado: ele entra no nome do link também.
  const accessibleName = card.imageAlt ? `${label}. ${card.imageAlt}` : label;
  // Só no editor: capa gravada sem ALT barra o save (schema + backend), então
  // o card com a pendência fica marcado para o editor achá-lo de relance.
  const missingAlt = !!onEditItem && !!card.image && !card.imageAlt;

  const media = (
    <span
      className={`relative ${BLOCO} w-full overflow-hidden rounded-xl bg-gray-100 dark:bg-night-700 ${
        tall ? 'h-full min-h-[16rem]' : slide ? 'aspect-[4/3]' : 'aspect-video'
      } ${missingAlt ? 'ring-4 ring-error ring-offset-2' : ''}`}
    >
      {image ? (
        <img
          src={comPrefixo(image.src)}
          alt={card.imageAlt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition duration-300 group-focus-within:scale-[1.03] group-focus-within:blur-sm group-hover:scale-[1.03] group-hover:blur-sm motion-reduce:transform-none motion-reduce:transition-none"
          onError={(event) => {
            // `maxresdefault` só existe para vídeo publicado em HD; `hqdefault`
            // existe sempre. Mesma troca do `OndeAtuaCard`.
            if (!card.videoId) return;
            const img = event.currentTarget;
            const fallback = youtubeThumb(card.videoId, 'high');
            if (img.src !== fallback) img.src = fallback;
          }}
        />
      ) : null}

      {isVideo && (
        <PlayBadge className="transition-opacity duration-300 group-focus-within:opacity-0 group-hover:opacity-0" />
      )}

      {/* Hover/foco: véu escuro sobre a imagem borrada e a chamada "Ver
          publicação" ao centro. Decorativo (`aria-hidden`): o nome acessível
          já vem do gatilho em volta; só aparece quando há link. */}
      {card.url && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-300 group-focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none"
        >
          <span className="rounded-lg bg-white px-4 py-2 text-base font-medium text-primary shadow-sm">
            Ver publicação
          </span>
        </span>
      )}

      {network && (
        <span className="absolute bottom-2 left-2 z-10 flex">
          <SocialIcon
            network={network}
            variant="brand"
            size={tall ? 40 : slide ? 28 : 36}
          />
        </span>
      )}
    </span>
  );

  return (
    <article
      className={`group relative flex flex-col gap-2 ${tall ? 'h-full' : ''}`}
    >
      {/* O card inteiro é clicável via `after:inset-0` no gatilho; o lápis é
          irmão posterior com `z-10` para continuar recebendo o clique. */}
      {card.url ? (
        isVideo ? (
          <button
            type="button"
            onClick={() =>
              onPlay({ id: card.videoId as string, title: card.title })
            }
            aria-label={`Assistir: ${accessibleName}`}
            className={`relative ${tall ? 'flex-1' : ''} cursor-pointer rounded-xl after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2`}
          >
            {media}
          </button>
        ) : (
          // O aviso de nova aba sai do rótulo: quem o acrescenta é o
          // MPFLink, e só quando a aba realmente troca.
          <MPFLink
            href={card.url}
            aria-label={accessibleName}
            className={`relative ${tall ? 'flex-1' : ''} ${BLOCO} cursor-pointer rounded-xl after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2`}
          >
            {media}
          </MPFLink>
        )
      ) : (
        // Slot vazio: só aparece no editor, e sem gatilho de clique.
        <span className={`relative ${tall ? 'flex-1' : ''} ${BLOCO}`}>
          {media}
        </span>
      )}

      {card.title ? (
        /* `min-h-[3rem]`: duas linhas de `text-base` (1.5rem de
           `line-height` cada — ver `--typography-body-medium-line-height`
           em `tokens/_css-vars.scss`). Sem a reserva, um título de 1 linha
           deixa o card mais baixo que o de 2; como este card é irmão de
           `AcompanheRedesSection` numa grade `auto-rows-fr`, a diferença
           de altura entre eles estica a linha inteira e o card "tall" (o
           de vídeo, que cresce com `h-full`) passa da borda dos vizinhos.
           Com a altura do título travada em 2 linhas — cortando um 3º com
           `line-clamp-2` — o card ocupa sempre a mesma altura, venha o
           título curto ou longo. */
        <p
          className={`line-clamp-2 min-h-[3rem] font-normal text-on-surface dark:text-night-100 ${
            slide ? 'text-sm md:text-base' : 'text-base'
          }`}
        >
          {card.title}
        </p>
      ) : (
        onEditItem && (
          <p className="min-h-[3rem] text-base text-gray-400 dark:text-night-400">
            Post {card.slot} sem conteúdo
          </p>
        )
      )}

      {missingAlt && (
        // Fora do link (botão dentro de `<a>` é interativo aninhado): irmão
        // posicionado sobre o canto da imagem, como o lápis.
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onEditItem(card.slot);
          }}
          className="absolute left-2 top-2 z-20 flex cursor-pointer items-center gap-1 rounded-md bg-error px-2 py-1 text-sm font-medium text-on-error shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-error"
        >
          <span aria-hidden="true">⚠</span>
          Post {card.slot} sem texto alternativo
        </button>
      )}

      {onEditItem && (
        <EditItemButton
          label={`Editar post ${card.slot}${card.title ? `: ${card.title}` : ''}`}
          onClick={() => onEditItem(card.slot)}
        />
      )}
    </article>
  );
};

export default RedeCard;
